import { buildFolderTree } from "@hq/core";
import { useFolders } from "@hq/client-react";
import { Button, cn, Dialog } from "@z/ui";
import { Check, Folder as FolderIcon, Inbox } from "lucide-react";
import { flattenFolderTree } from "./lib";

export type FolderPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Where the item is now; shown with a check and not offered again. */
  currentId: string | null;
  /** Folders that cannot be chosen, e.g. the folder being moved and its descendants. */
  excludeIds?: ReadonlySet<string>;
  onPick: (folderId: string | null) => void;
};

/** A flat, indented list of every live folder plus the root, for "Move to…" actions. */
export function FolderPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  currentId,
  excludeIds,
  onPick,
}: FolderPickerDialogProps) {
  const folders = useFolders().data ?? [];
  const rows = flattenFolderTree(buildFolderTree(folders), () => true).filter(
    (row) => !excludeIds?.has(row.folder.id),
  );
  const pick = (id: string | null) => {
    onPick(id);
    onOpenChange(false);
  };
  const optionClass = (current: boolean) =>
    cn(
      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-fg outline-none transition-colors duration-fast hover:bg-fg/6 focus-ring",
      current && "text-fg-muted",
    );
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          {description !== undefined && <Dialog.Description>{description}</Dialog.Description>}
        </Dialog.Header>
        <div
          role="listbox"
          aria-label="Destination folder"
          className="-mx-1 max-h-80 overflow-y-auto"
        >
          <button
            type="button"
            role="option"
            aria-selected={currentId === null}
            disabled={currentId === null}
            className={optionClass(currentId === null)}
            onClick={() => pick(null)}
          >
            <Inbox className="size-4 text-fg-muted" />
            <span className="flex-1 truncate">No folder</span>
            {currentId === null && <Check className="size-4" />}
          </button>
          {rows.map((row) => {
            const current = row.folder.id === currentId;
            return (
              <button
                key={row.folder.id}
                type="button"
                role="option"
                aria-selected={current}
                disabled={current}
                className={optionClass(current)}
                style={{ paddingLeft: `${8 + row.depth * 16}px` }}
                onClick={() => pick(row.folder.id)}
              >
                <FolderIcon className="size-4 text-fg-muted" />
                <span className="flex-1 truncate">{row.folder.name}</span>
                {current && <Check className="size-4" />}
              </button>
            );
          })}
        </div>
        <Dialog.Footer>
          <Dialog.Close render={<Button variant="secondary">Cancel</Button>} />
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
